// ============================================
// components/HeaderMenu.jsx - Scriptus Pharma Partner
// Design Glassmorphism consistente com Scriptus RxIA
// Cor Emerald Premium
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { 
  QrCode,
  History, 
  Settings, 
  CreditCard, 
  LogOut,
  ChevronDown,
  Building2,
  Sparkles,
  Package,
  Lock
} from 'lucide-react';

const HeaderMenu = () => {
  const { pharmacy, isPremium, isPartner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [aberto, setAberto] = useState(false);
  const menuRef = useRef(null);

  // Handler otimizado para logout
  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  }, [navigate]);

  // Fechar menu quando clicar fora - otimizado
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setAberto(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setAberto(false);
      }
    };

    if (aberto) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [aberto]);

  // Dados da farmácia memoizados
  const pharmacyData = React.useMemo(() => {
    const nomeCompleto = pharmacy?.companyData?.tradeName || pharmacy?.companyData?.name || 'Farmácia';
    const partesNome = nomeCompleto.split(' ');
    const nome = partesNome.length > 1 ? `${partesNome[0]} ${partesNome[1]}` : partesNome[0];
    
    // Usar isPartner para determinar o plano
    const plano = isPartner ? "Partner" : "Basic";

    return { nome, plano, nomeCompleto };
  }, [pharmacy, isPartner]);

  // Menu items de navegação principal
  const menuItems = React.useMemo(() => [
    {
      label: "Histórico",
      icon: <History className="w-4 h-4" />,
      action: () => navigate('/historico'),
      description: "Ver dispensações anteriores",
      path: "/historico",
      partnerOnly: false
    },
    {
      label: "Catálogo",
      icon: <Package className="w-4 h-4" />,
      action: () => navigate(isPartner ? '/catalogo-produtos' : '/assinatura?upgrade=partner'),
      description: isPartner ? "Seus produtos para médicos" : "Recurso Partner",
      path: "/catalogo-produtos",
      partnerOnly: true,
      badge: isPartner ? null : "Partner"
    }
  ], [navigate, isPartner]);

  // Menu items de configuração
  const configItems = React.useMemo(() => [
    {
      label: "Configurações",
      icon: <Settings className="w-4 h-4" />,
      action: () => navigate('/configuracoes'),
      description: "Ajustar preferências",
      path: "/configuracoes"
    },
    {
      label: "Assinatura",
      icon: <CreditCard className="w-4 h-4" />,
      action: () => navigate('/assinatura'),
      description: "Gerenciar plano",
      path: "/assinatura"
    }
  ], [navigate]);

  // Handler para navegação
  const handleNavigation = useCallback((action) => {
    setAberto(false);
    action();
  }, []);

  return (
    <>
      <div className="relative" ref={menuRef}>
        {/* Trigger Button */}
        <button
          onClick={() => setAberto(!aberto)}
          className={`
            group flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl sm:rounded-2xl transition-all duration-300
            ${aberto 
              ? 'glass-card-active shadow-lg scale-105' 
              : 'glass-card-subtle hover:glass-card-hover hover:shadow-md hover:scale-102'
            }
          `}
          aria-expanded={aberto}
          aria-haspopup="true"
        >
          {/* Avatar/Ícone da Farmácia */}
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center shadow-sm flex-shrink-0 ${
            isPartner 
              ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
              : 'bg-gradient-to-br from-emerald-500 to-teal-500'
          }`}>
            {isPartner ? (
              <Sparkles className="w-4 h-4 text-white" />
            ) : (
              <Building2 className="w-4 h-4 text-white" />
            )}
          </div>

          {/* Pharmacy Info - Responsivo */}
          <div className="hidden sm:flex flex-col items-start min-w-0 flex-1">
            <span className="font-semibold text-gray-900 text-sm sf-pro-text tracking-tight truncate max-w-32">
              {pharmacyData.nome}
            </span>
            <span className={`text-xs font-medium sf-pro-text tracking-wide leading-none ${
              isPartner ? 'text-purple-600' : 'text-emerald-600'
            }`}>
              {pharmacyData.plano}
            </span>
          </div>

          {/* Chevron */}
          <ChevronDown className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 transition-transform duration-300 flex-shrink-0 ${
            aberto ? 'rotate-180' : 'rotate-0'
          }`} />
        </button>

        {/* Dropdown Menu */}
        {aberto && (
          <>
            {/* Mobile Overlay com blur */}
            <div 
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm sm:bg-transparent sm:backdrop-blur-none" 
              onClick={() => setAberto(false)}
              aria-hidden="true"
            />
            
            <div 
              className="absolute right-0 top-full mt-2 w-80 sm:w-72 origin-top-right transform transition-all duration-300 ease-out z-50"
              role="menu"
              aria-orientation="vertical"
            >
              <div className="glass-menu rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
                
                {/* Header do Menu */}
                <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-white/10">
                  <div className="flex items-center space-x-2.5 sm:space-x-3">
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center shadow-sm flex-shrink-0 ${
                      isPartner 
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                        : 'bg-gradient-to-br from-emerald-500 to-teal-500'
                    }`}>
                      {isPartner ? (
                        <Sparkles className="w-4 h-4 text-white" />
                      ) : (
                        <Building2 className="w-4 h-4 text-white" />
                      )}
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm sf-pro-display tracking-tight line-clamp-1">
                        {pharmacyData.nomeCompleto}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium sf-pro-text tracking-wide ${
                          isPartner ? 'text-purple-600' : 'text-emerald-600'
                        }`}>
                          Plano {pharmacyData.plano}
                        </span>
                        {isPartner && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">
                            PRO
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Menu Principal - Navegação */}
                <div className="py-1">
                  <div className="px-3 sm:px-4 py-1.5">
                    <div className="text-xs font-semibold text-gray-500 sf-pro-text uppercase tracking-wider">
                      Navegação
                    </div>
                  </div>
                  
                  {/* Link para Dispensação (antigo Dashboard) */}
                  <button
                    onClick={() => handleNavigation(() => navigate('/dashboard'))}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 flex items-center space-x-2.5 sm:space-x-3 transition-all duration-300 group ${
                      location.pathname === '/dashboard' 
                        ? 'bg-emerald-50/60 border-r-2 border-emerald-600' 
                        : 'hover:bg-white/30 hover:backdrop-blur-lg'
                    }`}
                    role="menuitem"
                  >
                    <div className={`p-1.5 rounded-lg transition-all duration-300 flex-shrink-0 ${
                      location.pathname === '/dashboard' 
                        ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' 
                        : 'bg-gray-100/80 border border-gray-200/50 group-hover:bg-emerald-100/80 text-gray-600 group-hover:text-emerald-600 group-hover:shadow-md'
                    }`}>
                      <QrCode className="w-4 h-4" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className={`font-semibold text-sm sf-pro-text tracking-tight line-clamp-1 ${
                        location.pathname === '/dashboard' ? 'text-emerald-700' : 'text-gray-700'
                      }`}>
                        Dispensação
                      </div>
                      <div className="text-xs text-gray-500 sf-pro-text line-clamp-1">
                        Validar receita e gerar XML
                      </div>
                    </div>
                  </button>
                  
                  {menuItems.map((item, index) => {
                    const isActiveItem = location.pathname === item.path;
                    const isLocked = item.partnerOnly && !isPartner;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleNavigation(item.action)}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 flex items-center space-x-2.5 sm:space-x-3 transition-all duration-300 group ${
                          isActiveItem 
                            ? 'bg-emerald-50/60 border-r-2 border-emerald-600' 
                            : isLocked
                              ? 'hover:bg-purple-50/40 hover:backdrop-blur-lg'
                              : 'hover:bg-white/30 hover:backdrop-blur-lg'
                        }`}
                        role="menuitem"
                      >
                        <div className={`p-1.5 rounded-lg transition-all duration-300 flex-shrink-0 ${
                          isActiveItem 
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' 
                            : isLocked
                              ? 'bg-purple-100/80 border border-purple-200/50 group-hover:bg-purple-200/80 text-purple-600 group-hover:shadow-md'
                              : 'bg-gray-100/80 border border-gray-200/50 group-hover:bg-emerald-100/80 text-gray-600 group-hover:text-emerald-600 group-hover:shadow-md'
                        }`}>
                          {isLocked ? <Lock className="w-4 h-4" /> : item.icon}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold text-sm sf-pro-text tracking-tight line-clamp-1 ${
                              isActiveItem 
                                ? 'text-emerald-700' 
                                : isLocked 
                                  ? 'text-purple-700' 
                                  : 'text-gray-700'
                            }`}>
                              {item.label}
                            </span>
                            {item.badge && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm">
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <div className={`text-xs sf-pro-text line-clamp-1 ${
                            isLocked ? 'text-purple-500' : 'text-gray-500'
                          }`}>
                            {item.description}
                          </div>
                        </div>
                        {isLocked && (
                          <div className="flex-shrink-0">
                            <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Separador */}
                <div className="border-t border-white/10"></div>

                {/* Configurações e Conta */}
                <div className="py-1">
                  <div className="px-3 sm:px-4 py-1.5">
                    <div className="text-xs font-semibold text-gray-500 sf-pro-text uppercase tracking-wider">
                      Conta
                    </div>
                  </div>
                  {configItems.map((item, index) => {
                    const isActiveItem = location.pathname === item.path;
                    return (
                      <button
                        key={index}
                        onClick={() => handleNavigation(item.action)}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 flex items-center space-x-2.5 sm:space-x-3 transition-all duration-300 group ${
                          isActiveItem 
                            ? 'bg-emerald-50/60 border-r-2 border-emerald-600' 
                            : 'hover:bg-white/30 hover:backdrop-blur-lg'
                        }`}
                        role="menuitem"
                      >
                        <div className={`p-1.5 rounded-lg transition-all duration-300 flex-shrink-0 ${
                          isActiveItem 
                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' 
                            : 'bg-gray-100/80 border border-gray-200/50 group-hover:bg-emerald-100/80 text-gray-600 group-hover:text-emerald-600 group-hover:shadow-md'
                        }`}>
                          {item.icon}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className={`font-semibold text-sm sf-pro-text tracking-tight line-clamp-1 ${
                            isActiveItem ? 'text-emerald-700' : 'text-gray-700'
                          }`}>
                            {item.label}
                          </div>
                          <div className="text-xs text-gray-500 sf-pro-text line-clamp-1">
                            {item.description}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Separador */}
                <div className="border-t border-white/10"></div>

                {/* Logout */}
                <div className="py-1">
                  <button
                    onClick={() => {
                      setAberto(false);
                      handleLogout();
                    }}
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 flex items-center space-x-2.5 sm:space-x-3 hover:bg-red-50/40 hover:backdrop-blur-lg transition-all duration-300 group"
                    role="menuitem"
                  >
                    <div className="p-1.5 rounded-lg bg-red-100/80 border border-red-200/50 group-hover:bg-red-200/80 group-hover:shadow-md transition-all duration-300 flex-shrink-0">
                      <LogOut className="w-4 h-4 text-red-600" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-semibold text-red-600 text-sm sf-pro-text tracking-tight">
                        Sair
                      </div>
                      <div className="text-xs text-red-500 sf-pro-text">
                        Encerrar sessão
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Custom Styles */}
      <style>{`
        /* San Francisco Pro Font Stack */
        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* Glassmorphism - Trigger Button */
        .glass-card-subtle {
          background: rgba(255, 255, 255, 0.75);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(255, 255, 255, 0.4);
          box-shadow: 
            0 4px 16px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.7);
        }

        .glass-card-hover {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 
            0 8px 24px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .glass-card-active {
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 12px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }

        /* Menu Dropdown */
        .glass-menu {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 
            0 20px 64px rgba(0, 0, 0, 0.15),
            0 8px 32px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }

        /* Hover Effects */
        .hover\\:glass-card-hover:hover {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 
            0 8px 24px rgba(0, 0, 0, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.8);
        }

        .hover\\:scale-102:hover {
          transform: scale(1.02);
        }

        .scale-105 {
          transform: scale(1.05);
        }

        /* Line clamp utility */
        .line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }

        /* Smooth Transitions */
        * {
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Remove tap highlight on mobile */
        button {
          -webkit-tap-highlight-color: transparent;
          min-height: 44px;
        }

        /* Focus styles for accessibility */
        button:focus-visible {
          outline: 2px solid #10b981;
          outline-offset: 2px;
        }

        /* Mobile Optimizations */
        @media (max-width: 640px) {
          .absolute.right-0.top-full {
            width: calc(100vw - 1rem);
            max-width: 320px;
            right: 0.5rem;
            left: auto;
          }
          
          .glass-card-subtle {
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
          }
          
          .glass-card-hover {
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
          }
          
          .glass-menu {
            backdrop-filter: blur(20px);
            -webkit-backdrop-filter: blur(20px);
            border-radius: 0.75rem;
          }
          
          button {
            min-height: 48px;
          }
          
          .px-4 {
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }
        }

        /* Tablet optimizations */
        @media (min-width: 640px) and (max-width: 1024px) {
          .absolute.right-0.top-full {
            width: 288px;
          }
        }

        /* Very small screens */
        @media (max-width: 480px) {
          .w-7.h-7 {
            width: 1.5rem;
            height: 1.5rem;
          }
          
          .text-sm {
            font-size: 0.825rem;
          }
          
          .text-xs {
            font-size: 0.7rem;
          }
          
          .px-3 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          .mt-2 {
            margin-top: 0.25rem;
          }
        }
        
        /* Touch devices */
        @media (hover: none) {
          .hover\\:bg-white\\/30:hover {
            background-color: rgba(255, 255, 255, 0.25);
          }
          
          .hover\\:bg-red-50\\/40:hover {
            background-color: rgba(254, 242, 242, 0.3);
          }
        }

        /* High contrast mode */
        @media (prefers-contrast: high) {
          .glass-card-subtle, 
          .glass-card-hover, 
          .glass-card-active,
          .glass-menu {
            background: rgba(255, 255, 255, 0.98);
            border: 2px solid rgba(0, 0, 0, 0.2);
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Performance optimizations */
        * {
          will-change: auto;
        }

        /* Menu item hover */
        [role="menuitem"]:hover {
          transform: translateX(2px);
        }

        [role="menuitem"]:active {
          transform: translateX(1px) scale(0.99);
        }

        /* Prevent layout shift */
        .w-80 {
          width: 20rem;
        }

        @media (max-width: 640px) {
          .w-80 {
            width: calc(100vw - 1rem);
            max-width: 20rem;
          }
        }
      `}</style>
    </>
  );
};

export default HeaderMenu;